export interface StateFilterRangeProps {
  lte: number | undefined;
  gte: number | undefined;
}

export class StateFilterRange {
  lte: number | undefined;
  gte: number | undefined;

  constructor({ lte, gte }: StateFilterRangeProps) {
    this.lte = lte;
    this.gte = gte;
  }

  public formatToSend() {
    return {
      lte: this.lte ?? 0,
      gte: this.gte ?? 0
    };
  }
}

export interface StateFilterDateRangeProps {
  lte: string;
  gte: string;
}

export class StateFilterDateRange {
  lte: string;
  gte: string;

  constructor({ lte, gte }: StateFilterDateRangeProps) {
    this.lte = lte;
    this.gte = gte;
  }

  public formatToSend() {
    return {
      lte: this.lte,
      gte: this.gte
    };
  }
}

export class StateFilterBoolean {
  value: boolean;

  constructor(value: boolean) {
    this.value = value;
  }

  public formatToSend() {
    return this.value;
  }
}

export class StateFilterString {
  value: string;

  constructor(value: string) {
    this.value = value;
  }

  public formatToSend() {
    return this.value;
  }
}

export class StateFilterArray {
  value: string[] | number[];

  constructor(value: string[] | number[]) {
    this.value = value;
  }

  public formatToSend() {
    return this.value;
  }
}

export type StateFilter =
  | StateFilterRange
  | StateFilterDateRange
  | StateFilterBoolean
  | StateFilterString
  | StateFilterArray;

export type StateFilterValue =
  | { gte: number | undefined; lte: number | undefined }
  | { gte: string; lte: string }
  | boolean
  | string
  | number[]
  | string[];

export class ConfigJson {
  private filterMap: Map<string, StateFilter>;

  constructor(filters: Record<string, StateFilterValue> | undefined) {
    if (filters) {
      this.filterMap = this.parseToConfigJson(filters);
    } else this.filterMap = new Map<string, StateFilter>();
  }

  public createFilter(value: StateFilterValue): StateFilter {
    if (Array.isArray(value)) {
      return new StateFilterArray(value);
    }
    if (typeof value === "boolean") {
      return new StateFilterBoolean(value);
    }
    if (typeof value === "string") {
      return new StateFilterString(value);
    }
    if (typeof value === "object" && value !== null) {
      if ("gte" in value && "lte" in value) {
        if (
          (typeof value.gte === "number" || value.gte === undefined) &&
          (typeof value.lte === "number" || value.lte === undefined)
        ) {
          return new StateFilterRange(value as StateFilterRangeProps);
        } else if (
          typeof value.gte === "string" &&
          typeof value.lte === "string"
        ) {
          return new StateFilterDateRange(value as StateFilterDateRangeProps);
        }
      }
    }
    throw new Error("Contém um filtro inválido");
  }

  public getFilter(key: string): StateFilter | undefined {
    return this.filterMap.get(key);
  }

  public getFilterValue(key: string): StateFilterValue | undefined {
    return this.filterMap.get(key)?.formatToSend();
  }

  public getRangeValue(
    key: string
  ):
    | { gte: string | number | undefined; lte: string | number | undefined }
    | undefined {
    const filter = this.filterMap.get(key);
    if (
      filter instanceof StateFilterRange ||
      filter instanceof StateFilterDateRange
    ) {
      return { gte: filter.gte, lte: filter.lte };
    }
    return undefined;
  }

  public getMultiValue(key: string): any[] {
    const value = this.getFilterValue(key);
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.length) {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }

  public setValue(key: string, value: StateFilterValue) {
    this.filterMap.set(key, this.createFilter(value));
  }

  public setRangeValue(
    key: string,
    side: "gte" | "lte",
    value: string,
    isDate?: boolean
  ) {
    const prev = this.getFilterValue(key);
    const baseValue =
      prev && typeof prev === "object" && "gte" in prev && "lte" in prev
        ? prev
        : isDate
          ? { gte: "", lte: "" }
          : { gte: undefined, lte: undefined };
    const nextValue = {
      ...baseValue,
      [side]: isDate ? value : value === "" ? undefined : Number(value)
    };
    this.setValue(key, nextValue as StateFilterValue);
  }

  public updateArrayValue(key: string, value: string | number) {
    const prev = this.getFilterValue(key);
    const list: any[] = Array.isArray(prev) ? prev : [];
    const isInclude = list.find((option) => option === value);
    const newList = isInclude
      ? list.filter((option) => option !== value)
      : [...list, value];
    this.setValue(key, newList as StateFilterValue);
  }

  public deleteFilter(key: string): boolean {
    return this.filterMap.delete(key);
  }

  public parseToConfigJson(configJson: Record<string, StateFilterValue>) {
    return new Map(
      Object.entries(configJson).map(([key, value]) => [
        key,
        this.createFilter(value)
      ])
    );
  }

  public formatToSend() {
    const formatted: Record<string, StateFilterValue> = {};
    this.filterMap.forEach((value, key) => {
      formatted[key] = value.formatToSend();
    });
    return Object.keys(formatted).length ? formatted : undefined;
  }
}
